import { createAsyncThunk } from "@reduxjs/toolkit";
import { apiClient } from "../../utils";
import { API_ENDPOINTS } from "../../constants/common";

const stripHost = (url?: string | null) => {
    if (!url) return null;
    try {
        if (/^https?:\/\//i.test(url)) {
            const u = new URL(url);
            return `${u.pathname}${u.search}`;
        }
        return url;
    } catch {
        const idx = url.indexOf("/api/");
        return idx >= 0 ? url.slice(idx) : url;
    }
};

const AdminActionTypes = {
    UPLOAD_FILES: "files/upload",
    GET_USERS: "admin/getUsers",
    GET_SUBSCRIBED_USERS: "admin/getSubscribedUsers",
    DELETE_USER: "admin/deleteUser",
    SET_USER_SUBSCRIPTION: "admin/setUserSubscription",
    CANCEL_USER_SUBSCRIPTION: "admin/cancelUserSubscription",
    UPDATE_USER: "admin/updateUser",
    RESET_USER_PASSWORD: "admin/resetUserPassword",
};

type GetUsersArgs = {
    page?: number;
    perPage?: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc";
};

type DeleteUserArgs = { userId: number | string };

type SetUserSubscriptionArgs = {
    userId: number | string;
    planId: number | string;
    expiresAt: string;
    planCode?: string;
};

type CancelUserSubscriptionArgs = { userId: number | string };
type UpdateUserPayload = {
    password?: string;
    name?: string;
    color?: string;
    telegram?: string;
    whatsapp?: string;
    instagram?: string;
    phone?: number | string;
};

type UpdateUserArgs = {
    userId: number | string;
    payload: UpdateUserPayload;
};

type ResetUserPasswordArgs = { email: string };

const normalizeUpdateUserPayload = (payload: UpdateUserPayload): Partial<UpdateUserPayload> => {
    const out: Partial<UpdateUserPayload> = {};

    const putStr = (key: keyof UpdateUserPayload) => {
        const v = payload[key];
        if (typeof v !== "string") return;
        const trimmed = v.trim();
        if (!trimmed) return; // ✅ пустое не отправляем
        (out as any)[key] = trimmed;
    };

    // строки
    putStr("password");
    putStr("name");
    putStr("color");
    putStr("telegram");
    putStr("whatsapp");
    putStr("instagram");

    // phone
    const p = payload.phone;
    if (typeof p === "number") {
        if (Number.isFinite(p) && p > 0) out.phone = p; // ✅ только валидное
    } else if (typeof p === "string") {
        const digits = p.replace(/\D/g, "");
        if (digits.length) out.phone = digits; // ✅ если хочешь хранить строкой
        // если бэк ждёт number:
        // const n = Number(digits);
        // if (Number.isFinite(n) && n > 0) out.phone = n;
    }

    return out;
};

export const AdminActionCreator = {
  uploadFiles: createAsyncThunk(
    AdminActionTypes.UPLOAD_FILES,
    async ({ files, setProgress }) => {
      try {
        const response = await apiClient.post(
          API_ENDPOINTS.uploadFiles,
          {
            files,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => setProgress(progressEvent),
          },
        );
        return response.data;
      } catch (err) {
        return err;
      }
    },
  ),
    getSubscribedUsers: createAsyncThunk(
        AdminActionTypes.GET_SUBSCRIBED_USERS,
        async (
            { page = 1, perPage = 50 }: { page?: number; perPage?: number },
            { rejectWithValue }: any,
        ) => {
            try {
                const res = await apiClient.get(API_ENDPOINTS.subscribedUsers, {
                    params: {
                        page,
                        perPage, // 👈 важно: именно perPage
                    },
                });

                const users = res?.data?.data ?? [];
                const meta = res?.data?.meta ?? {};
                const links = res?.data?.links ?? {};

                return {
                    users,

                    // paginator meta (Laravel)
                    total: typeof meta.total === "number" ? meta.total : null,
                    page:
                        typeof meta.current_page === "number"
                            ? meta.current_page
                            : page,
                    perPage:
                        typeof meta.per_page === "number"
                            ? meta.per_page
                            : perPage,
                    lastPage:
                        typeof meta.last_page === "number"
                            ? meta.last_page
                            : null,

                    // ссылки
                    nextUrl: links?.next ?? null,
                    prevUrl: links?.prev ?? null,
                };
            } catch (err: any) {
                return rejectWithValue(
                    err?.response?.data || {
                        message: err?.message || "Unknown error",
                    },
                );
            }
        },
    ),
    getUsers: createAsyncThunk(
        AdminActionTypes.GET_USERS,
        async (
            { page = 1, perPage = 50, search = "", sort, order }: GetUsersArgs,
            { rejectWithValue }: any,
        ) => {
            try {
                const res = await apiClient.get(API_ENDPOINTS.users, {
                    params: {
                        page,
                        perPage,
                        ...(search ? { search } : {}),
                        ...(sort ? { sort } : {}),
                        ...(order ? { order } : {}),
                    },
                });

                const users = res?.data?.data ?? [];
                const meta = res?.data?.meta ?? {};
                const links = res?.data?.links ?? {};

                return {
                    users,
                    total: typeof meta.total === "number" ? meta.total : null,
                    page: typeof meta.current_page === "number" ? meta.current_page : page,
                    perPage: typeof meta.per_page === "number" ? meta.per_page : perPage,
                    lastPage: typeof meta.last_page === "number" ? meta.last_page : null,
                    nextUrl: stripHost(links?.next ?? null),
                    prevUrl: stripHost(links?.prev ?? null),

                    query: { page, perPage, search, sort: sort ?? null, order: order ?? null },
                };
            } catch (err: any) {
                return rejectWithValue(
                    err?.response?.data || { message: err?.message || "Unknown error" },
                );
            }
        },
    ),
    deleteUser: createAsyncThunk(
        AdminActionTypes.DELETE_USER,
        async ({ userId }: DeleteUserArgs, { rejectWithValue }: any) => {
            try {
                const res = await apiClient.delete(`${API_ENDPOINTS.deleteUser}/${userId}`);

                // иногда бэк возвращает {success:true} / {message:"..."} — не важно,
                // редьюсеру главное знать, кого удалили
                return {
                    userId,
                    data: res?.data ?? null,
                };
            } catch (err: any) {
                return rejectWithValue(
                    err?.response?.data || { message: err?.message || "Unknown error" },
                );
            }
        },
    ),
    setUserSubscription: createAsyncThunk(
        AdminActionTypes.SET_USER_SUBSCRIPTION,
        async ({ userId, planId, expiresAt, planCode }: SetUserSubscriptionArgs, { rejectWithValue }: any) => {
            try {
                const res = await apiClient.post(API_ENDPOINTS.createUserSubscription(userId), {
                    planId,
                    expiresAt,
                });

                return { userId, planId, planCode: planCode ?? null, expiresAt, data: res?.data ?? null };
            } catch (err: any) {
                return rejectWithValue(err?.response?.data || { message: err?.message || "Unknown error" });
            }
        },
    ),
    cancelUserSubscription: createAsyncThunk(
        AdminActionTypes.CANCEL_USER_SUBSCRIPTION,
        async ({ userId }: CancelUserSubscriptionArgs, { rejectWithValue }: any) => {
            try {
                const res = await apiClient.delete(API_ENDPOINTS.createUserSubscription(userId));

                return {
                    userId,
                    data: res?.data ?? null,
                };
            } catch (err: any) {
                return rejectWithValue(
                    err?.response?.data || { message: err?.message || "Unknown error" },
                );
            }
        },
    ),
    updateUser: createAsyncThunk(
        AdminActionTypes.UPDATE_USER,
        async ({ userId, payload }: UpdateUserArgs, { rejectWithValue }: any) => {
            try {
                const cleanPayload = normalizeUpdateUserPayload(payload);

                // ✅ если вообще нечего отправлять — сразу выходим
                if (!Object.keys(cleanPayload).length) {
                    return { userId, user: null, data: null, skipped: true };
                }

                const res = await apiClient.put(API_ENDPOINTS.updateUser(userId), cleanPayload);

                return {
                    userId,
                    user: res?.data?.data ?? res?.data ?? null,
                    data: res?.data ?? null,
                    skipped: false,
                };
            } catch (err: any) {
                return rejectWithValue(err?.response?.data || { message: err?.message || "Unknown error" });
            }
        },
    ),
    resetUserPassword: createAsyncThunk(
        AdminActionTypes.RESET_USER_PASSWORD,
        async ({ email }: ResetUserPasswordArgs, { rejectWithValue }: any) => {
            try {
                const cleanEmail = email.trim();

                if (!cleanEmail) {
                    return rejectWithValue({ message: "Email is required" });
                }

                const res = await apiClient.post(API_ENDPOINTS.resetUserPassword, {
                    email: cleanEmail,
                });

                return {
                    email: cleanEmail,
                    data: res?.data ?? null,
                };
            } catch (err: any) {
                return rejectWithValue(
                    err?.response?.data || { message: err?.message || "Unknown error" },
                );
            }
        },
    ),
};
